'use client';

import { useCallback } from 'react';
import { toast as sonnerToast } from 'sonner';

/**
 * Chakra-like toast options for migration compatibility.
 */
export interface UseToastOptions {
  title?: string;
  description?: string | React.ReactNode;
  status?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  isClosable?: boolean;
  /** Ignored when using Sonner; kept for Chakra API compatibility. */
  position?: string;
}

/**
 * Returns a toast function compatible with Chakra's useToast() API.
 * Uses Sonner under the hood (shadcn/sonner). Use this instead of @chakra-ui/react useToast.
 */
export function useToast() {
  const toastFn = useCallback((options: UseToastOptions) => {
    const {
      title = '',
      description = '',
      status = 'info',
      duration = 5000,
    } = options;
    const primary: string | React.ReactNode =
      title ||
      (typeof description === 'string' ? description : description || '');
    if (!primary && !description) return;

    const opts =
      title && description ? { description, duration } : { duration };

    switch (status) {
      case 'success':
        sonnerToast.success(primary, opts);
        break;
      case 'error':
        sonnerToast.error(primary, opts);
        break;
      case 'warning':
        sonnerToast.warning(primary, opts);
        break;
      default:
        sonnerToast(primary, opts);
    }
  }, []);

  const toastWithClose = Object.assign(toastFn, {
    closeAll: () => sonnerToast.dismiss(),
  });
  return toastWithClose as typeof toastFn & { closeAll: () => void };
}
